import DoneOutlineOutlinedIcon from "@mui/icons-material/DoneOutlineOutlined";
import dayjs from "dayjs";
import { sum } from "lodash";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MaterialRequestFilterType, valueToText } from "../../../configs/filterTypes";
import {
    WarningCancelMaterialRequest,
    WarningDeclineReasonMaterialRequest,
} from "../../../configs/materialRequest";
import { MaterialRequest } from "../../../data/entities";
import { MATERIAL_REQUEST_STATUS, ROLE } from "../../../data/enums";
import { AuthenticationContext } from "../../../provider/AuthenticationProvider";
import { ConfirmModal } from "../../../shared/components/ConfirmModal";
import { NormalText } from "../../../shared/components/ConfirmModal/Styled";
import DocxPreviewModal from "../../../shared/components/DocxPreviewModal";
import DeclineModal from "../../../shared/components/GenericTextboxModal";
import LoadingButton from "../../../shared/components/LoadingButton";
import PageLoader from "../../../shared/components/LoadingPage";
import DeclineReasonModal from "../../../shared/components/ReasonTextModal";
import Status from "../../../shared/components/Status";
import { api } from "../../../shared/hooks/api";
import { TitleText, TitleWrapper } from "../../../shared/styled/TitleBar";
import Calendar from "../../../shared/svg/Calendar";
import UserCheckIcon from "../../../shared/svg/UserCheckIcon";
import Template from "../../../templates/PMS - Phiếu lãnh vật tư.docx";
import { buildDocxData } from "../../../utils/docxBuilder";
import { materialRequestPreviewDescriptor } from "../../../utils/docxMappings";
import { generateDocument, generateDocumentBlob } from "../../../utils/exportUtils";
import { summarizeQuantitiesByUnit } from "../../../utils/functions";
import { error, success } from "../../../utils/toast";
import { getStatusColorTheme } from "../../MaterialRequests/Content/MaterialRequestTable";
import { ButtonRow } from "../../ProductOrder/create/Styled";
import {
    HeaderLeft,
    IconWrap,
    MetaBar,
    MetaDividerV,
    MetaItem,
    MetaLabel,
    MetaRow,
    MetaValue,
    TitleRow,
    UrgentDot,
    UrgentText,
} from "../../SaleOrder/detail/Styled";
import { SingleMaterialRequest } from "../single";
import { StyledBackdrop } from "../single/AddMaterialModal/Styled";
import { MaterialRequestWrapper, StyledButton } from "../Styled";
import { ExportMaterialModal } from "./ExportMaterialModal";
import StatusProgress from "./StatusProgress";
import MaterialRequestTabNavigation from "./TabBar";

interface ValidationResult {
    isValid: boolean;
    errors: string[];
}

const processMaterialRequest = (materialRequest: MaterialRequest) => {
    let materials = [];
    if (materialRequest) {
        materials = materialRequest.materials.map((item: any) => {
            if (item.material) {
                return {
                    ...item.material,
                    missing_quantity: item.quantity,
                    request_quantity: item.quantity,
                    provider_name: item.provider_name,
                    provider_price: item.provider_price,
                    exported_quantity: item.exported_quantity,
                };
            }
            return item;
        });
        materialRequest.created_at = dayjs(materialRequest.created_at).format("YYYY-MM-DD");
    }
    return { ...materialRequest, materials };
};

const prepareMaterialRequest = (materialRequest: any) => {
    const processedMaterials = materialRequest.materials.map((material: any) => ({
        material_id: material.id,
        quantity: material.missing_quantity,
        provider_name: material.provider_name,
        provider_price: material.provider_price,
    }));

    const materialOrderPrice = Math.round(
        sum(
            materialRequest.materials.map(
                (item: any) => item.provider_price * item.missing_quantity,
            ),
        ),
    );

    const processedData = {
        ...materialRequest,
        materials: processedMaterials,
        factory_staff: materialRequest.factory_staff?.id,
        first_storage_keeper: materialRequest.first_storage_keeper?.id,
        second_storage_keeper: materialRequest.second_storage_keeper?.id,
        factory_manager: materialRequest.factory_manager?.id,
        total_price: materialOrderPrice,
    };

    return processedData;
};

export const MaterialRequestDetail = () => {
    const { materialRequestId } = useParams();
    const navigate = useNavigate();
    const [{ data, isLoading: isDataLoading, setLocalData }, fetchMaterialRequest] = api.get(
        `/materials/requests/${materialRequestId}/`,
    );
    const materialRequest: MaterialRequest = useMemo(() => processMaterialRequest(data), [data]);

    const [{ isUpdating }, setNewData] = api.patch(`/materials/requests/${materialRequestId}/`);

    const [{ isUpdating: isUpdatingCompleteExport }, setCompleteExportData] = api.patch(
        `/materials/requests/${materialRequestId}/complete-export/`,
    );

    const [isView, setIsView] = useState(true);
    const [isOpenDeclineModal, setIsOpenDeclineModal] = useState(false);
    const [showDeclineReasonModal, setShowDeclineReasonModal] = useState(false);
    const [isOpenCompleteExportModal, setIsOpenCompleteExportModal] = useState(false);
    const isLoading: boolean = isDataLoading;
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);

    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isEnhancedExportModalOpen, setIsEnhancedExportModalOpen] = useState(false);
    const [
        {
            data: materialExports,
            isLoading: isFetchingMaterialExports,
            error: fetchMaterialExportError,
            setLocalUrl: setGetMaterialExport,
        },
        fetchMaterialExports,
    ] = api.get(`/materials/requests/${materialRequest?.id}/exports/`);

    const {
        user: { role },
    } = useContext(AuthenticationContext);

    const canSendToStorage = () => {
        const isRoleAllowed = [
            ROLE.MANAGER,
            ROLE.FACTORY_MANAGER_ONE,
            ROLE.FACTORY_MANAGER_TWO,
            ROLE.DEPUTY_FACTORY_MANAGER_ONE,
            ROLE.DEPUTY_FACTORY_MANAGER_TWO,
        ].includes(role);

        return isRoleAllowed && materialRequest.status === MATERIAL_REQUEST_STATUS.OPEN;
    };

    const areStorageKeepersAssigned = (materialRequest: MaterialRequest): boolean => {
        const requiredStorageKeepers = materialRequest.required_storage_keepers || [];

        return requiredStorageKeepers.every((keeper) => {
            if (keeper === "first_storage_keeper") {
                return materialRequest.first_storage_keeper !== null;
            } else if (keeper === "second_storage_keeper") {
                return materialRequest.second_storage_keeper !== null;
            }
            return true;
        });
    };

    const canExportFromStorage = () => {
        const isRoleAllowed = [
            ROLE.MANAGER,
            ROLE.STORAGE_KEEPER,
            ROLE.FIRST_STORAGE_KEEPER,
            ROLE.SECOND_STORAGE_KEEPER,
        ].includes(role);
        const isExporting = [MATERIAL_REQUEST_STATUS.EXPORTING].includes(materialRequest.status);

        if (role === ROLE.FIRST_STORAGE_KEEPER) {
            return isRoleAllowed && isExporting && !materialRequest.is_first_storage_exported;
        } else if (role === ROLE.SECOND_STORAGE_KEEPER) {
            return isRoleAllowed && isExporting && !materialRequest.is_second_storage_exported;
        }

        return isRoleAllowed && isExporting;
    };

    const hasIssuedMaterials = useMemo(() => {
        const exportedFromList = Array.isArray(materialExports)
            && materialExports.some((item: any) => Number(item.quantity || 0) > 0);
        const exportedFromItems = Array.isArray(materialRequest.materials)
            && materialRequest.materials.some((item: any) => Number(item.exported_quantity || 0) > 0);
        return exportedFromList || exportedFromItems;
    }, [materialExports, materialRequest.materials]);

    const onClickCancelMaterialRequest = () => {
        if (hasIssuedMaterials) {
            error("Không thể hủy phiếu lãnh vì vật tư đã được xuất kho.");
            return;
        }
        setIsOpenDeclineModal(true);
    };

    useEffect(() => {
        setGetMaterialExport(`/materials/requests/${materialRequest.id}/exports/`);
    }, [materialRequest.id, setGetMaterialExport]);

    const validateExportData = (): ValidationResult => {
        const errors: string[] = [];

        if (materialRequest.materials.length === 0) {
            errors.push("Danh sách vật tư không được để trống.");
        }

        return {
            isValid: errors.length === 0,
            errors,
        };
    };

    const onClickSendingToStorageKeeper = async () => {
        const validation = validateExportData();
        if (!validation.isValid) {
            alert(
                "Vui lòng điền đầy đủ thông tin trước khi xem trước:\n\n" +
                validation.errors.join("\n\n"),
            );
            return;
        }

        try {
            const result = summarizeQuantitiesByUnit(materialRequest.materials || []);
            const exportData = { ...materialRequest, sum_quantity: result };

            const docData = buildDocxData(exportData, materialRequestPreviewDescriptor);
            const blob = await generateDocumentBlob(docData, Template);
            setPreviewBlob(blob);
            setIsPreviewOpen(true);
        } catch (e) {
            console.error("Failed to generate import preview DOCX:", e);
            alert("Không thể tạo bản xem trước tài liệu.");
        }
    };

    const onConfirmSendingToStorageKeeper = async () => {
        if (isUpdating) return;
        try {
            const preparedMaterialRequest: any = prepareMaterialRequest(materialRequest);
            preparedMaterialRequest.status = MATERIAL_REQUEST_STATUS.EXPORTING;
            await setNewData(preparedMaterialRequest);
            await fetchMaterialRequest();
            success("THÀNH CÔNG", "Gửi cho thủ kho thành công");
            setIsPreviewOpen(false);
        } catch (err) {
            error("Gửi cho thủ kho thất bại", err);
        }
    };

    const onClickUpdateMaterialRequest = async () => {
        if (isUpdating) return;
        try {
            const preparedData = prepareMaterialRequest(materialRequest);

            await setNewData(preparedData);
            await fetchMaterialRequest();
            setIsView(true);
            success("THÀNH CÔNG", "Cập nhật phiếu lãnh thành công");
        } catch (err) {
            error("Cập nhật phiếu lãnh thất bại", err);
        }
    };

    const onClickCompleteExport = async () => {
        if (isUpdatingCompleteExport) return;
        try {
            let preparedMaterialRequest: any = prepareMaterialRequest(materialRequest);

            // Storage_exported with null value mean the storage don't need to exported
            if (role === ROLE.MANAGER) {
                if (preparedMaterialRequest.is_first_storage_exported !== null) {
                    preparedMaterialRequest.is_first_storage_exported = true;
                }
                if (preparedMaterialRequest.is_second_storage_exported !== null) {
                    preparedMaterialRequest.is_second_storage_exported = true;
                }
            } else if (role === ROLE.FIRST_STORAGE_KEEPER) {
                if (preparedMaterialRequest.is_first_storage_exported !== null) {
                    preparedMaterialRequest.is_first_storage_exported = true;
                }
            } else if (role === ROLE.SECOND_STORAGE_KEEPER) {
                if (preparedMaterialRequest.is_second_storage_exported !== null) {
                    preparedMaterialRequest.is_second_storage_exported = true;
                }
            }

            await setCompleteExportData(preparedMaterialRequest);
            await fetchMaterialRequest();
            setIsOpenCompleteExportModal(false);
            success("THÀNH CÔNG", "Hoàn thành xuất kho thành công");
        } catch (err) {
            error("Hoàn thành xuất kho thất bại", err);
        }
    };

    const onClickDecline = async (reason?: string) => {
        if (isUpdating) return;
        try {
            const preparedMaterialRequest: any = prepareMaterialRequest(materialRequest);
            preparedMaterialRequest.status = MATERIAL_REQUEST_STATUS.CANCELLED;
            preparedMaterialRequest.note = reason;
            await setNewData(preparedMaterialRequest);
            await fetchMaterialRequest();
            setIsOpenDeclineModal(false);
            success("THÀNH CÔNG", "Đã hủy phiếu lãnh thành công");
        } catch (err) {
            error("Hủy phiếu lãnh thất bại", err);
        }
    };

    const isEditable = () => {
        return (
            materialRequest.status === MATERIAL_REQUEST_STATUS.OPEN &&
            [ROLE.MANAGER, ROLE.FACTORY_MANAGER_ONE, ROLE.FACTORY_MANAGER_TWO, ROLE.DEPUTY_FACTORY_MANAGER_ONE, ROLE.DEPUTY_FACTORY_MANAGER_TWO].includes(role)
        );
    };

    const canCancelMaterialRequest = () => {
        return (
            [MATERIAL_REQUEST_STATUS.OPEN, MATERIAL_REQUEST_STATUS.EXPORTING].includes(materialRequest.status) &&
            [ROLE.MANAGER, ROLE.FACTORY_MANAGER_ONE, ROLE.FACTORY_MANAGER_TWO, ROLE.DEPUTY_FACTORY_MANAGER_ONE, ROLE.DEPUTY_FACTORY_MANAGER_TWO].includes(role)
        );
    };

    const onToggleDeclineReasonModal = () => {
        setShowDeclineReasonModal((prev) => !prev);
    };

    const onDownloadMaterialRequest = () => {
        const roundTo5Decimal = (value: number): number => {
            if (!Number.isFinite(value)) return 0;
            const factor = 10 ** 5;
            return Math.round((value + Number.EPSILON) * factor) / factor;
        };

        const items = [...(materialRequest.materials || [])];

        const firstStorage = items.filter((item: any) => item.storage === "Kho 1");
        const secondStorage = items.filter((item: any) => item.storage === "Kho 2");

        const genericData: any = {
            request_name: materialRequest.request_name || "",
            created_at: materialRequest.created_at || "",
            sale_order: (materialRequest as any).sale_order || "",
            department: (materialRequest as any).department || "",
        };

        if (firstStorage.length > 0) {
            const firstStorageData = {
                ...genericData,
                materials: firstStorage.map((item: any, index: number) => ({
                    index: index + 1,
                    name: item.name || "",
                    quantity: item.request_quantity || "",
                    code: item.material_code || item.code || item.id || "",
                    measurement_unit: item.measurement_unit || "",
                    conversion_quantity: item.secondary_measurement_unit ? roundTo5Decimal(item.request_quantity / item.conversion_rate) : "",
                    secondary_measurement_unit: item.secondary_measurement_unit || "",
                })),
                sum_quantity: summarizeQuantitiesByUnit(firstStorage),
            };
            generateDocument(
                firstStorageData,
                Template,
                `don_lanh_vat_tu_${materialRequest.request_name}_kho_1`,
            );
        }

        if (secondStorage.length > 0) {
            const secondStorageData = {
                ...genericData,
                materials: secondStorage.map((item: any, index: number) => ({
                    index: index + 1,
                    name: item.name || "",
                    quantity: item.request_quantity || "",
                    code: item.material_code || item.code || item.id || "",
                    measurement_unit: item.measurement_unit || "",
                    conversion_quantity: item.secondary_measurement_unit ? roundTo5Decimal(item.request_quantity / item.conversion_rate) : "",
                    secondary_measurement_unit: item.secondary_measurement_unit || "",
                })),
                sum_quantity: summarizeQuantitiesByUnit(secondStorage),
            };
            generateDocument(
                secondStorageData,
                Template,
                `don_lanh_vat_tu_${materialRequest.request_name}_kho_2`,
            );
        }
    };

    const materialRequestInfoTab = isLoading ? (
        <PageLoader />
    ) : (
        <MaterialRequestWrapper>
            <SingleMaterialRequest
                materialRequest={materialRequest}
                mergeMaterialRequest={(updatedData: any) =>
                    setLocalData({
                        ...data,
                        ...updatedData,
                    })
                }
                fetchMaterialRequest={fetchMaterialRequest}
                isView={isView}
            />
        </MaterialRequestWrapper>
    );

    if (isLoading) return <PageLoader />;

    return (
        <>
            <MaterialRequestWrapper>
                {isOpenDeclineModal && (
                    <DeclineModal
                        open={isOpenDeclineModal}
                        title={WarningCancelMaterialRequest.title}
                        contentText={WarningCancelMaterialRequest.context}
                        reasonText={WarningCancelMaterialRequest.reason}
                        handleConfirm={onClickDecline}
                        handleClose={() => setIsOpenDeclineModal(false)}
                        buttonText="Huỷ phiếu lãnh"
                        isLoading={isUpdating}
                    />
                )}

                {isOpenCompleteExportModal && (
                    <ConfirmModal
                        open={isOpenCompleteExportModal}
                        handleConfirm={onClickCompleteExport}
                        handleClose={() => setIsOpenCompleteExportModal(false)}
                        isLoading={isUpdatingCompleteExport}
                        confirmButtonText="Đồng ý"
                    >
                        <TitleText>Thông báo xác nhận</TitleText>
                        <NormalText>
                            Bạn có chắc chắn muốn hoàn thành xuất kho cho phiếu này không?
                        </NormalText>
                    </ConfirmModal>
                )}

                <DeclineReasonModal
                    open={showDeclineReasonModal}
                    title={WarningDeclineReasonMaterialRequest.title}
                    contentText={[materialRequest.note]}
                    handleClose={onToggleDeclineReasonModal}
                />

                <TitleWrapper>
                    <HeaderLeft>
                        <TitleRow>
                            <TitleText>
                                {materialRequest.request_name
                                    ? materialRequest.request_name
                                    : materialRequest.id}
                            </TitleText>
                            <Status
                                colorTheme={getStatusColorTheme(materialRequest.status)}
                                text={valueToText(
                                    MaterialRequestFilterType,
                                    "status",
                                    materialRequest.status,
                                )}
                                width="124px"
                            />
                        </TitleRow>

                        {(materialRequest?.is_urgent || materialRequest?.due_date) && (
                            <MetaRow>
                                <MetaBar>
                                    {materialRequest?.is_urgent && (
                                        <>
                                            <UrgentDot />
                                            <UrgentText>Khẩn cấp</UrgentText>
                                            {materialRequest?.due_date && <MetaDividerV />}
                                        </>
                                    )}
                                    {materialRequest?.due_date && (
                                        <MetaItem>
                                            <IconWrap>
                                                <Calendar />
                                            </IconWrap>
                                            <MetaLabel>Hạn</MetaLabel>
                                            <MetaValue>
                                                {dayjs(materialRequest.due_date).format(
                                                    "DD/MM/YYYY",
                                                )}
                                            </MetaValue>
                                        </MetaItem>
                                    )}
                                </MetaBar>
                            </MetaRow>
                        )}
                    </HeaderLeft>

                    <ButtonRow>
                        <StyledButton
                            variant="outlined"
                            onClick={() => onDownloadMaterialRequest()}
                        >
                            Tải phiếu lãnh
                        </StyledButton>

                        <StyledButton
                            variant="outlined"
                            onClick={() =>
                                navigate("/material-requests/create", {
                                    state: { duplicateData: materialRequest },
                                })
                            }
                        >
                            Nhân bản
                        </StyledButton>

                        {!isView && (
                            <>
                                <StyledButton variant="text" onClick={() => setIsView(true)}>
                                    Huỷ bỏ
                                </StyledButton>
                                <LoadingButton
                                    variant="contained"
                                    text="Cập nhật phiếu lãnh"
                                    isLoading={isUpdating}
                                    disabled={isUpdating}
                                    onClick={onClickUpdateMaterialRequest}
                                />
                            </>
                        )}

                        {isView && (
                            <>
                                {canCancelMaterialRequest() && (
                                    <StyledButton
                                        variant="text"
                                        onClick={onClickCancelMaterialRequest}
                                    >
                                        Huỷ phiếu lãnh
                                    </StyledButton>
                                )}
                                {materialRequest.status !== MATERIAL_REQUEST_STATUS.EXPORTING && (
                                    isEditable() && (
                                        <StyledButton
                                            variant="text"
                                            onClick={() => setIsView(false)}
                                        >
                                            Chỉnh sửa
                                        </StyledButton>
                                    )
                                )}

                                {canSendToStorage() && (
                                    <LoadingButton
                                        tooltipText={"Cần chọn thủ kho Review"}
                                        text={"Gửi tới thủ kho "}
                                        disabled={
                                            !areStorageKeepersAssigned(materialRequest) ||
                                            isLoading ||
                                            isUpdating
                                        }
                                        isLoading={isUpdating}
                                        onClick={onClickSendingToStorageKeeper}
                                        icon={<UserCheckIcon />}
                                    />
                                )}

                                {canExportFromStorage() && (
                                    <>
                                        <ButtonRow>
                                            <StyledButton
                                                variant="contained"
                                                onClick={() =>
                                                    materialRequest && setIsExportModalOpen(true)
                                                }
                                                disabled={!materialRequest}
                                            >
                                                Tiến hành xuất kho
                                            </StyledButton>
                                        </ButtonRow>
                                        <ButtonRow>
                                            <StyledButton
                                                startIcon={<DoneOutlineOutlinedIcon />}
                                                variant="contained"
                                                onClick={() => setIsOpenCompleteExportModal(true)}
                                            >
                                                Hoàn thành xuất kho
                                            </StyledButton>
                                        </ButtonRow>
                                    </>
                                )}
                            </>
                        )}
                    </ButtonRow>
                </TitleWrapper>

                {isLoading || isFetchingMaterialExports ? (
                    <PageLoader />
                ) : (
                    <>
                        <StatusProgress status={materialRequest.status} />

                        <MaterialRequestTabNavigation
                            materialRequestInfoTab={materialRequestInfoTab}
                            materialRequest={materialRequest}
                        />
                    </>
                )}
            </MaterialRequestWrapper>

            <DocxPreviewModal
                open={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                blob={previewBlob}
                title="Xem trước phiếu lãnh vật tư"
                BackdropComponent={StyledBackdrop}
                backdropProps={{ timeout: 300 }}
                debug
                actions={
                    <LoadingButton
                        variant="contained"
                        onClick={onConfirmSendingToStorageKeeper}
                        disabled={isUpdating}
                        isLoading={isUpdating}
                        text="Gửi tới thủ kho"
                    />
                }
                download={onDownloadMaterialRequest}
            />

            <ExportMaterialModal
                open={isExportModalOpen}
                handleClose={() => setIsExportModalOpen(false)}
                materialRequest={materialRequest}
                materialExports={materialExports}
                handleConfirm={() => {
                    setIsExportModalOpen(false);
                    fetchMaterialExports();
                    fetchMaterialRequest();
                }}
            />
        </>
    );
};
